import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/ui/use-toast';
import type { KnowledgeBaseShare } from '@/types/supabase';

export function useKnowledgeBaseSharing() {
  const { user } = useAuth();
  const { toast } = useToast();

  const getKnowledgeBaseShares = useCallback(async (knowledgeBaseId: string): Promise<KnowledgeBaseShare[]> => {
    try {
      const { data, error } = await supabase
        .from('knowledge_base_shares')
        .select('*')
        .eq('knowledge_base_id', knowledgeBaseId);

      if (error) throw error;

      const shares = (data ?? []) as Array<KnowledgeBaseShare & { user_id: string; access_level: string }>;
      const userIds = [...new Set(shares.map((share) => share.user_id))];

      if (userIds.length === 0) {
        return shares.map((share) => ({
          ...share,
          access_level: share.access_level as 'View' | 'Edit' | 'Manage',
        }));
      }

      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, full_name')
        .in('id', userIds);

      if (profilesError) throw profilesError;

      const userNameMap = new Map(
        ((profiles ?? []) as Array<{ id: string; full_name: string | null }>)
          .map((profile) => [profile.id, profile.full_name || 'Unknown User'])
      );

      return shares.map((share) => ({
        ...share,
        user_name: userNameMap.get(share.user_id) || 'Unknown User',
        access_level: share.access_level as 'View' | 'Edit' | 'Manage',
      }));
    } catch (error) {
      console.error('Error fetching knowledge base shares:', error);
      return [];
    }
  }, []);

  const shareKnowledgeBase = useCallback(async (
    knowledgeBaseId: string,
    userId: string,
    accessLevel: 'View' | 'Edit' | 'Manage'
  ) => {
    if (!user) return false;

    try {
      const { data: existingShare, error: checkError } = await supabase
        .from('knowledge_base_shares')
        .select('*')
        .eq('knowledge_base_id', knowledgeBaseId)
        .eq('user_id', userId)
        .maybeSingle();

      if (checkError) throw checkError;

      if (existingShare) {
        const { error: updateError } = await (supabase as any)
          .from('knowledge_base_shares')
          .update({
            access_level: accessLevel,
            shared_by: user.id,
            shared_at: new Date().toISOString(),
          })
          .eq('id', (existingShare as { id: string }).id);

        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await (supabase as any)
          .from('knowledge_base_shares')
          .insert({
            knowledge_base_id: knowledgeBaseId,
            user_id: userId,
            access_level: accessLevel,
            shared_by: user.id,
          });

        if (insertError) throw insertError;
      }

      return true;
    } catch (error) {
      console.error('Error sharing knowledge base:', error);
      toast({
        title: 'Error',
        description: 'Failed to share knowledge base',
        variant: 'destructive',
      });
      return false;
    }
  }, [toast, user]);

  const updateShareAccessLevel = useCallback(async (
    shareId: string,
    accessLevel: 'View' | 'Edit' | 'Manage'
  ) => {
    try {
      const { error } = await (supabase as any)
        .from('knowledge_base_shares')
        .update({ access_level: accessLevel })
        .eq('id', shareId);

      if (error) throw error;

      return true;
    } catch (error) {
      console.error('Error updating access level:', error);
      toast({
        title: 'Error',
        description: 'Failed to update access level',
        variant: 'destructive',
      });
      return false;
    }
  }, [toast]);

  const removeShare = useCallback(async (shareId: string) => {
    try {
      const { error } = await supabase
        .from('knowledge_base_shares')
        .delete()
        .eq('id', shareId);

      if (error) throw error;

      return true;
    } catch (error) {
      console.error('Error removing share:', error);
      toast({
        title: 'Error',
        description: 'Failed to remove share',
        variant: 'destructive',
      });
      return false;
    }
  }, [toast]);

  return {
    getKnowledgeBaseShares,
    shareKnowledgeBase,
    updateShareAccessLevel,
    removeShare,
  };
}
